import { supabase } from '../../supabaseClient';
import type { RemotePresenceState } from '../../types/presentationRemote';
import { REALTIME_WS_URL } from './realtimeConfig';
import {
  onlineWarn as warn,
  presenceSignature,
  type OnlineTransport,
  type OnlineTransportCallbacks,
  type OnlineTransportOptions,
} from './onlineTransport';

/**
 * Cloudflare Workers + Durable Objects 구현 (cloudflare/realtime 참고).
 * - 방: /ws/remote/<userId>?sid=<sessionId>, 인증은 Sec-WebSocket-Protocol "cp-rt.v1, auth.<Supabase JWT>"
 * - presence 대체: 서버가 소켓별 상태를 보관하고 welcome/presence(update|leave) 이벤트로 알림 — 주기 heartbeat 없음
 * - 주기 keepalive ping 없음(비용 0). 예기치 않게 끊기면 1회만 즉시(1초 후) 자동 재시도하고,
 *   그래도 안 되면 'disconnected' 상태로 멈춘다 → UI 의 '다시 연결' 버튼(reconnect)으로 재연결.
 * - 토큰 갱신 시 재인증 프레임 전송
 */
const SUBPROTOCOL = 'cp-rt.v1';
/** 예기치 않은 종료 후 1회 자동 재시도 지연 */
const AUTO_RETRY_DELAY_MS = 1000;
const TRACK_DEBOUNCE_MS = 1500;
/** 서버 종료 코드 (cloudflare/realtime/src/protocol.ts 와 동일) */
const CLOSE_REPLACED = 4000;
const CLOSE_UNAUTHORIZED = 4401;
const CLOSE_FORBIDDEN = 4403;
const CLOSE_FLOOD = 4429;

type ServerFrame =
  | { t: 'welcome'; sessionId: string; roster: RemotePresenceState[] }
  | { t: 'presence'; op: 'update'; state: RemotePresenceState }
  | { t: 'presence'; op: 'leave'; sessionId: string }
  | { t: 'msg'; from: string; data: unknown }
  | { t: 'error'; code: string };

export function createCloudflareOnlineTransport(
  opts: OnlineTransportOptions,
  cb: OnlineTransportCallbacks
): OnlineTransport {
  const { userId, sessionId } = opts;
  let ws: WebSocket | null = null;
  let ready = false;
  let enabled = false;
  let disposed = false;
  /** 마지막 welcome 이후 자동 재시도를 이미 썼는지 */
  let autoRetried = false;
  let retryTimer: number | null = null;
  /** 다시 시도하면 안 되는 종료(권한 없음·다른 연결로 교체) */
  let halted = false;
  let unauthorizedRetried = false;
  let trackedState: RemotePresenceState | null = null;
  let sentSig = '';
  let trackTimer: number | null = null;
  const peers = new Map<string, RemotePresenceState>();

  const emitPresence = () => cb.onPresence(Array.from(peers.values()));
  const clearPresence = () => {
    if (!peers.size) return;
    peers.clear();
    emitPresence();
  };

  const clearRetry = () => {
    if (retryTimer != null) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
  };
  const sendFrame = (obj: unknown): boolean => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(obj));
      return true;
    } catch {
      return false;
    }
  };

  const trackNow = () => {
    if (trackTimer != null) {
      window.clearTimeout(trackTimer);
      trackTimer = null;
    }
    if (!ready || !trackedState) return;
    const sig = presenceSignature(trackedState);
    if (sig === sentSig) return;
    if (sendFrame({ t: 'track', state: { ...trackedState, lastSeen: Date.now() } })) sentSig = sig;
  };

  const closeSocket = (code = 1000, reason = 'client_close') => {
    const s = ws;
    ws = null;
    ready = false;
    sentSig = '';
    if (trackTimer != null) {
      window.clearTimeout(trackTimer);
      trackTimer = null;
    }
    clearPresence();
    if (s) {
      s.onopen = null;
      s.onmessage = null;
      s.onclose = null;
      s.onerror = null;
      try {
        s.close(code, reason);
      } catch {
        /* ignore */
      }
    }
  };

  /** 1회만 자동 재시도, 이후엔 수동('다시 연결') */
  const retryOnceOrStop = () => {
    if (disposed || !enabled || halted || retryTimer != null) return;
    if (autoRetried) {
      cb.onStatus('disconnected');
      return;
    }
    autoRetried = true;
    cb.onStatus('reconnecting');
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      void connect();
    }, AUTO_RETRY_DELAY_MS);
  };

  const onFrame = (raw: string) => {
    let f: ServerFrame;
    try {
      f = JSON.parse(raw) as ServerFrame;
    } catch {
      return;
    }
    if (!f || typeof f !== 'object') return;
    switch (f.t) {
      case 'welcome': {
        ready = true;
        autoRetried = false;
        unauthorizedRetried = false;
        peers.clear();
        (Array.isArray(f.roster) ? f.roster : []).forEach((p) => {
          if (p && typeof p.sessionId === 'string') peers.set(p.sessionId, p);
        });
        cb.onStatus('connected');
        emitPresence();
        sentSig = '';
        trackNow();
        return;
      }
      case 'presence':
        if (f.op === 'update' && f.state && typeof f.state.sessionId === 'string') {
          peers.set(f.state.sessionId, f.state);
          emitPresence();
        } else if (f.op === 'leave' && typeof f.sessionId === 'string') {
          if (peers.delete(f.sessionId)) emitPresence();
        }
        return;
      case 'msg':
        cb.onEnvelope(f.data);
        return;
      case 'error':
        if (f.code === 'rate_limited') warn('전송 속도 제한 — 일부 메시지가 버려졌습니다.');
        return;
      default:
    }
  };

  const connect = async () => {
    if (disposed || !enabled || halted) return;
    closeSocket();
    cb.onStatus(autoRetried ? 'reconnecting' : 'connecting');
    let token = '';
    try {
      const { data } = await supabase.auth.getSession();
      token = data.session?.access_token ?? '';
    } catch {
      token = '';
    }
    if (disposed || !enabled || halted) return;
    if (!token) {
      warn('세션 없음 — 로그인 후 다시 시도하세요.');
      cb.onStatus('error');
      return;
    }
    const url = `${REALTIME_WS_URL}/ws/remote/${encodeURIComponent(userId)}?sid=${encodeURIComponent(sessionId)}`;
    let s: WebSocket;
    try {
      s = new WebSocket(url, [SUBPROTOCOL, `auth.${token}`]);
    } catch (e) {
      warn('WebSocket 생성 실패', e instanceof Error ? e.message : String(e));
      retryOnceOrStop();
      return;
    }
    ws = s;
    s.onmessage = (ev: MessageEvent) => {
      if (ws !== s) return;
      if (typeof ev.data === 'string') onFrame(ev.data);
    };
    s.onclose = (ev: CloseEvent) => {
      if (ws !== s) return;
      ws = null;
      ready = false;
      sentSig = '';
      clearPresence();
      if (disposed || !enabled) return;
      if (ev.code === CLOSE_FORBIDDEN) {
        warn('원격 제어 권한 없음(유료 이용권 확인 필요) — 원격 연결을 중지합니다.', ev.reason);
        halted = true;
        cb.onStatus('error');
        return;
      }
      if (ev.code === CLOSE_REPLACED) {
        halted = true;
        cb.onStatus('disconnected');
        return;
      }
      if (ev.code === CLOSE_UNAUTHORIZED) {
        if (unauthorizedRetried) {
          warn('인증 실패 — 다시 로그인해 주세요.');
          cb.onStatus('error');
          return;
        }
        unauthorizedRetried = true;
        void connect(); /* 새 토큰으로 1회 즉시 재시도 */
        return;
      }
      if (ev.code === CLOSE_FLOOD || ev.code === 1013) {
        /* 과부하·속도 제한 — 자동 재시도 없이 수동으로 */
        halted = true;
        cb.onStatus('limited');
        return;
      }
      retryOnceOrStop();
    };
    s.onerror = () => {
      /* close 이벤트에서 처리 */
    };
  };

  /** '다시 연결' 버튼 */
  const reconnect = () => {
    if (disposed || !enabled) return;
    if (ws && ws.readyState === WebSocket.CONNECTING) return;
    clearRetry();
    halted = false;
    autoRetried = false;
    unauthorizedRetried = false;
    void connect();
  };

  const { data: authSub } = supabase.auth.onAuthStateChange((event, session) => {
    if (disposed) return;
    if (event === 'SIGNED_OUT' || !session) {
      closeSocket();
      cb.onStatus('error');
      return;
    }
    if (!enabled) return;
    if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
      if (ready) sendFrame({ t: 'auth', token: session.access_token });
      /* 끊긴 상태에서의 재로그인은 자동 재연결하지 않음 — 버튼으로 */
    }
  });

  return {
    start: () => {
      if (disposed || enabled) return;
      enabled = true;
      halted = false;
      autoRetried = false;
      void connect();
    },
    stop: () => {
      if (!enabled) return;
      enabled = false;
      clearRetry();
      closeSocket();
    },
    isReady: () => ready,
    reconnect,
    send: async (env) => sendFrame({ t: 'send', data: env, ...(env.targetSessionId ? { to: env.targetSessionId } : {}) }),
    track: (state) => {
      trackedState = state;
      if (!ready) return;
      if (presenceSignature(state) === sentSig) return;
      if (!sentSig) trackNow();
      else {
        if (trackTimer != null) window.clearTimeout(trackTimer);
        trackTimer = window.setTimeout(trackNow, TRACK_DEBOUNCE_MS);
      }
    },
    dispose: () => {
      if (disposed) return;
      disposed = true;
      enabled = false;
      clearRetry();
      try {
        authSub.subscription.unsubscribe();
      } catch {
        /* ignore */
      }
      closeSocket();
    },
  };
}
